import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import { CertificationButton } from "./CertificationButton";
import { CertificationScrollableCell } from "./CertificationScrollableCell";
import { useCanManageCertificaciones } from "../permissions/useCertificationPermissions";

export type CertificationTableColumn<TRow> = {
  key: string;
  header: string;
  render: (row: TRow) => ReactNode;
  className?: string;
  scrollable?: boolean;
  requiresManage?: boolean;
};

export type CertificationControlledPagination = {
  currentPage: number;
  lastPage: number;
  from: number | null;
  to: number | null;
  total: number;
  onPageChange: (page: number) => void;
};

type Props<TRow> = {
  title: string;
  eyebrow?: string;
  countLabel: string;
  rows: TRow[];
  columns: CertificationTableColumn<TRow>[];
  getRowKey: (row: TRow) => string | number;
  loading: boolean;
  error: string | null;
  emptyTitle: string;
  emptyDescription?: string;
  createLabel?: string;
  onCreate?: () => void;
  onRetry: () => void;
  pageSize?: number;
  pagination?: CertificationControlledPagination;
  tableClassName?: string;
};

export function CertificationDataTable<TRow>({
  title,
  eyebrow = "Revisión rápida",
  countLabel,
  rows,
  columns,
  getRowKey,
  loading,
  error,
  emptyTitle,
  emptyDescription = "No hay registros para los criterios seleccionados.",
  createLabel,
  onCreate,
  onRetry,
  pageSize = 40,
  pagination,
  tableClassName = "",
}: Props<TRow>) {
  const canManage = useCanManageCertificaciones();
  const [localPage, setLocalPage] = useState(1);
  const controlled = pagination !== undefined;
  const total = controlled ? pagination.total : rows.length;
  const currentPage = controlled ? pagination.currentPage : localPage;
  const lastPage = controlled
    ? Math.max(1, pagination.lastPage)
    : Math.max(1, Math.ceil(total / pageSize));
  const from = controlled
    ? (pagination.from ?? 0)
    : total === 0
      ? 0
      : (currentPage - 1) * pageSize + 1;
  const to = controlled
    ? (pagination.to ?? 0)
    : total === 0
      ? 0
      : Math.min(total, currentPage * pageSize);
  const pageRows = useMemo(
    () => controlled
      ? rows
      : rows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [controlled, currentPage, pageSize, rows],
  );
  const orderedColumns = useMemo(() => {
    const actionColumns = columns.filter(
      (column) => column.key === "acciones" && (canManage || column.requiresManage === false),
    );
    const dataColumns = columns.filter((column) => column.key !== "acciones");
    return [...actionColumns, ...dataColumns];
  }, [canManage, columns]);

  useEffect(() => {
    if (controlled) return;
    setLocalPage((page) => Math.min(page, Math.max(1, Math.ceil(rows.length / pageSize))));
  }, [controlled, pageSize, rows.length]);

  const changePage = (page: number) => {
    const nextPage = Math.min(Math.max(1, page), lastPage);
    if (controlled) {
      pagination.onPageChange(nextPage);
      return;
    }
    setLocalPage(nextPage);
  };

  return (
    <section className="iso27001-table-card">
      <div className="iso27001-table-header">
        <div>
          <div className="iso27001-table-eyebrow">{eyebrow}</div>
          <h3>{title}</h3>
        </div>
        <div className="iso27001-table-header-actions">
          <span className="iso27001-table-count">{loading ? "Cargando..." : countLabel}</span>
          {canManage && onCreate && createLabel ? (
            <CertificationButton variant="primary" icon={<Plus size={16} />} onClick={onCreate}>
              {createLabel}
            </CertificationButton>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="iso27001-feedback-state">
          <div className="iso27001-feedback-title">No se pudo cargar la tabla</div>
          <p>{error}</p>
          <CertificationButton variant="secondary" icon={<RefreshCw size={15} />} onClick={onRetry}>
            Reintentar
          </CertificationButton>
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="iso27001-feedback-state">
          <Loader2 size={22} className="animate-spin" />
          <div className="iso27001-feedback-title">Cargando registros</div>
          <p>Estamos recuperando los datos de esta tabla.</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="iso27001-feedback-state">
          <div className="iso27001-feedback-title">{emptyTitle}</div>
          <p>{emptyDescription}</p>
          {canManage && onCreate && createLabel ? (
            <CertificationButton variant="primary" icon={<Plus size={16} />} onClick={onCreate}>
              {createLabel}
            </CertificationButton>
          ) : null}
        </div>
      ) : (
        <>
          <div className="iso27001-table-scroll">
            <table className={`iso27001-table ${tableClassName}`.trim()}>
              <thead>
                <tr>
                  {orderedColumns.map((column) => (
                    <th key={column.key} className={column.className}>{column.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={getRowKey(row)} className="iso27001-data-row">
                    {orderedColumns.map((column) => (
                      <td key={column.key} className={column.className}>
                        {column.key === "acciones" || column.scrollable === false
                          ? column.render(row)
                          : <CertificationScrollableCell>{column.render(row)}</CertificationScrollableCell>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="iso27001-pagination">
            <div className="iso27001-pagination-meta">Mostrando {from}-{to} de {total}</div>
            <div className="iso27001-pagination-controls">
              <CertificationButton
                variant="secondary"
                onClick={() => changePage(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
              >
                Anterior
              </CertificationButton>
              <span>Página {currentPage} de {lastPage}</span>
              <CertificationButton
                variant="secondary"
                onClick={() => changePage(currentPage + 1)}
                disabled={currentPage >= lastPage || loading}
              >
                Siguiente
              </CertificationButton>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
